import React from 'react';

import {
  JoinOptions,
  ParticipantInfo,
} from '@voxeet/voxeet-web-sdk/types/models/Options';
import DolbyConference from '@voxeet/voxeet-web-sdk/types/models/Conference';
import { Resolution } from '@pureweb/platform-sdk';

import { SpatialType } from '../../../../../interfaces/webSocketConnectionInfo';
import {
  IMediaStream,
  IParticipant,
  IParticipantRegion,
} from '../../../../../interfaces/participants';
import { IEvent } from '../../../../../interfaces/event';
import { IRoom } from '../../../../../interfaces/rooms';
import { ParticipantPosition } from '../interfaces';
import { IWebsocketMessageFlag } from '../../../providers/MytaverseEventProvider/interfaces';

export enum ConferenceError {
  MaxCapacityError = 'MaxCapacityError',
  NotAllowedError = 'NotAllowedError',
}

export type GetConferenceConfigType = (params: {
  spatialType: SpatialType;
  dolbySpatialAudioScale: number;
}) => {
  audioConfig: JoinOptions;
  conferenceScale: number | null;
};

export type GetConferenceAliasType = (params: {
  currentRegion: IParticipantRegion | null;
  currentEvent: IEvent;
  currentRoom: IRoom;
  gameSessionId: string;
}) => string;

export interface IConferenceQueue {
  currentEvent: IEvent;
  currentRoom: IRoom;
  currentRegion: IParticipantRegion | null;
}

export interface IConferenceContext {
  isDolbyInitialized: boolean;
  isConferenceInitialized: boolean;

  openSession: (participant: ParticipantInfo) => Promise<void>;

  conference: DolbyConference | null;
  streamQuality: Resolution;
  setStreamQuality: (quality: any) => void;
  leaveConference: () => Promise<void>;
  createAndJoinToConference: (
    currentEvent: IEvent,
    currentRoom: IRoom,
    currentRegion: IParticipantRegion | null,
  ) => Promise<void>;
  isFirstPersonView: IWebsocketMessageFlag;
  setIsFirstPersonView: React.Dispatch<
    React.SetStateAction<IWebsocketMessageFlag>
  >;
  microphones: any[];
  speakers: any[];
  cameras: any[];
  speakingParticipantIds: string[];
  previewingParticipant: IParticipant | null;
  setPreviewingParticipant: React.Dispatch<
    React.SetStateAction<IParticipant | null>
  >;
  selectMicrophone: (microphoneId: string) => void;
  selectSpeaker: (speakerId: string) => void;
  disabledPersonView: boolean;
  setDisabledPersonView: React.Dispatch<React.SetStateAction<boolean>>;
  selectCamera: (cameraId: string) => void;
  spatialTypeName: SpatialType | null;
  muted: boolean;
  videoStarted: boolean;
  toggleVideo: () => void;
  setFullscreen: () => void;
  isFullscreen: boolean;
  setScreenMediaStreams: React.Dispatch<React.SetStateAction<IMediaStream[]>>;
  sharingMedia: string[];
  setSharingMedia: React.Dispatch<React.SetStateAction<string[]>>;
  setVideoStarted: React.Dispatch<React.SetStateAction<boolean>>;
  startShareDolbyCameraToUE: React.MutableRefObject<boolean>;
  shareWebcamToUEMediaStream: React.MutableRefObject<IMediaStream | null>;
  ueWebcamScreenName: string | null;
  setUEWebcamScreenName: React.Dispatch<React.SetStateAction<string | null>>;
  ueScreenScreenName: string | null;
  setUEScreenScreenName: React.Dispatch<React.SetStateAction<string | null>>;
  changingDevice: boolean;
  setParticipantPosition: (participantPosition: ParticipantPosition) => void;
  isConferenceListener: boolean;

  cameraMediaStreams: IMediaStream[];
  screenMediaStreams: IMediaStream[];
  activeMicroDeviceId: string;

  startShareDolbyCamera: () => void;
  stopShareDolbyCamera: () => void;

  ueDolbyMediaStreams: IDolbyMediaStream[];
  ueScreenMediaStreams: IDolbyMediaStream[];
  ueWebcamMediaStreams: IDolbyMediaStream[];

  handleDolbyWSMessages: (lastJSONMessage: any) => void;
}

export interface IDolbyMediaStream {
  mediaStreamId: string;
  videoTrackId: string;
  ueScreenName: string;
  owner: {
    id: string;
    name: string;
  };
  source: string;
  muted: boolean;
  eventId: string;
  roomId: string;
  participantId: string;
  dolbyParticipantId: string;
  conferenceId: string;
  region?: string;
}
